import type { ReactNode } from "react";
import { createGameControllerContext } from "@rosette/royalur_react/src/game/createGameControllerContext.js";
import {
    GameContextProvider,
    useGameSource,
} from "@rosette/royalur_react/src/game/GameContext.js";
import { DiceControllerProvider, useDiceController } from "@rosette/web_components/src/game/dice/DiceUIContext.js";
import type { LobbySettings } from "@rosette/ts/src/business/game/LobbySettings.js";
import { PlayerStateControllerProvider, usePlayerStateController } from "@rosette/web_components/src/game/playerstate/PlayerStateUIContext.js";
import { BoardControllerProvider, useBoardController } from "@rosette/web_components/src/game/board/BoardUIContext.js";
import { AgentThread } from "@rosette/web_components/src/game/agent/AgentThread.js";
import type { GameSource } from "@rosette/ts/src/business/game/controller/source/GameSource.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import type { LobbyStatus } from "@rosette/ts/src/business/game/controller/status/LobbyStatus.js";
import { matomo } from "@/components/Matomo.js";
import { GameThemeProvider } from "@/components/game/theme/GameThemeContext.js";
import type { GameAPI } from "@rosette/ts/src/business/api/GameAPI.js";
import type { APIGamePreferences, APIUser } from "@rosette/ts/src/business/api/api_schema.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { ReactionControllerProvider, useReactionController } from "@rosette/web_components/src/game/reactions/ReactionUIContext.js";
import { useIsEpochTimeInFuture } from "@rosette/web_utils/src/time/useIsEpochTimeInFuture.js";
import { WebSocketConnection } from "@rosette/ts/src/business/game/server/WebSocketConnection.js";
import type { LobbyPlayer } from "@rosette/ts/src/business/lobby/LobbyPlayer.js";
import type { GamePlayers } from "@rosette/ts/src/business/api/game/GamePlayers.js";
import { BotLobbyPlayer } from "@rosette/ts/src/business/lobby/BotLobbyPlayer.js";
import { GameThemeType } from "@rosette/ts/src/business/game/theme/GameThemeType.js";
import type { GamePlatformServices } from "@rosette/ts/src/business/game/GamePlatformServices.js";
import { createGameSource as createSharedGameSource } from "@rosette/ts/src/business/game/controller/source/createGameSource.js";
import {
    browserStorageAdapter,
    readBrowserStorage,
} from "@rosette/web_utils/src/storage/browserStorage.js";

export {
    useGameClientControls,
    useGameSource,
} from "@rosette/royalur_react/src/game/GameContext.js";


export function createGameSource(
    user: APIUser | null,
    settings: LobbySettings,
    initialClientControls: GameClientControls,
    initialPreferences: APIGamePreferences,
    gameAPI: GameAPI,
): GameSource {
    const services: GamePlatformServices = {
        analytics: matomo,
        gameAPI,
        clientControls: initialClientControls,
        storage: browserStorageAdapter,
        botExecution: {
            createAgent(botType, rules) {
                if (botType.isExpensive()) {
                    return new AgentThread(botType);
                }
                return botType.createAgent(rules);
            },
        },
    };
    return createSharedGameSource({ user, settings, initialPreferences, services });
}


function readDefaultThemeType(): GameThemeType {
    const disabledThemeType = GameThemeType.getByNameOrNull(
        readBrowserStorage("disabled-theme-type"),
    );

    const now = new Date();
    for (const themeType of GameThemeType.values()) {
        if (themeType === disabledThemeType)
            continue;

        if (themeType.isInDateRange(now))
            return themeType;
    }
    return GameThemeType.DEFAULT;
}


export const {
    GameControllerProvider,
    useGameController,
    useOptionalGameController,
    useGameDirective,
    usePlayGameController,
    useAnalysisGameController,
    usePerfectGameController,
} = createGameControllerContext({
    useGameSource,
    usePlayerStateController,
    useReactionController,
    useDiceController,
    useBoardController,
    getDefaultThemeType: readDefaultThemeType,
});


export function useIsPlayerConnected(lobbyStatus: LobbyStatus, player: LobbyPlayer): boolean {
    const gameData = player.getGameData();
    const lastPingTimeMs = (
        gameData ? lobbyStatus.getPlayerLastPingTimeMs(gameData.getPlayerNo()) : 0
    );
    const hasRecentPing = useIsEpochTimeInFuture(lastPingTimeMs + WebSocketConnection.DISCONNECT_TIME_MS);
    return (
        !gameData || player instanceof BotLobbyPlayer
        || !lobbyStatus.hasPingTimes() || hasRecentPing
    );
}


export function useArePlayersConnected(lobbyStatus: LobbyStatus, players: GamePlayers): {
    p1Connected: boolean;
    p2Connected: boolean;
} {
    const p1Connected = useIsPlayerConnected(lobbyStatus, players.getPlayer1());
    const p2Connected = useIsPlayerConnected(lobbyStatus, players.getPlayer2());
    return { p1Connected, p2Connected };
}


/**
 * Includes the dependencies required for the game controller.
 */
function GameDependenciesProvider({ children }: { children: ReactNode }) {
    return (
        <BoardControllerProvider>
            <PlayerStateControllerProvider>
                <ReactionControllerProvider>
                    <DiceControllerProvider>
                        {children}
                    </DiceControllerProvider>
                </ReactionControllerProvider>
            </PlayerStateControllerProvider>
        </BoardControllerProvider>
    );
}


export interface GameControllerThemeProps {
    children: ReactNode;
}

function GameControllerThemeProvider({ children }: GameControllerThemeProps) {
    const controller = useGameController();
    const themeType = useRune(controller.getTheme());
    return (
        <GameThemeProvider themeType={themeType}>
            {children}
        </GameThemeProvider>
    );
}


export interface GameContextProps {
    gameSource: GameSource;
    clientControls: GameClientControls;
    children: ReactNode;
}

/**
 * Includes the game controller and all its dependencies.
 */
export function GameUIContext({ gameSource, clientControls, children }: GameContextProps) {
    return (
        <GameContextProvider
            gameSource={gameSource}
            clientControls={clientControls}>
            <GameDependenciesProvider>
                <GameControllerProvider>
                    <GameControllerThemeProvider>
                        {children}
                    </GameControllerThemeProvider>
                </GameControllerProvider>
            </GameDependenciesProvider>
        </GameContextProvider>
    );
}
